import React from 'react';
import intl from 'react-intl-universal';
import { Button } from 'semantic-ui-react';
import { DeviceMonitoringStatus, RoomSystem } from 'typings/types';
import { formatDateTimeFromUnix, secondsToMilliseconds } from 'utils/date-time-utils';
import _get from 'lodash/get';
import MonitoringRow from './MonitoringRow';
import classes from './MaintenanceTabComponent.scss';
import Icon from '../../Common/Icon';

interface Props {
  canReboot: boolean;
  showReboot: boolean;
  canReset: boolean;
  showReset: boolean;
  canPerformUpgradeManagement: boolean;
  roomSystem: RoomSystem;
  hasMonitoringData: boolean;
  deviceMonitoring: DeviceMonitoringStatus;
  monitoringStatusUpdatedTimestamp: number | null;
  errorCount: number;
  reboot: () => void;
  reset: () => void;
}

const MaintenanceTabComponent = (props: Props) => {
  const {
    deviceMonitoring,
    reboot,
    reset,
    hasMonitoringData,
    errorCount,
    canPerformUpgradeManagement,
    showReboot,
    canReboot,
    showReset,
    canReset,
    monitoringStatusUpdatedTimestamp
  } = props;
  return (
    <div className={classes.container}>
      {hasMonitoringData && (
        <div className={classes.pane}>
          <div className={classes.contentContainer}>
            <div className={classes.paneHeader}>
              <div className={classes.paneTitle}>
                {intl.get('monitoring')}
              </div>
              {monitoringStatusUpdatedTimestamp && (
                <div className={classes.paneHeaderStatusItalic}>
                  {intl.get('statusUpdated', { dateAndTime: formatDateTimeFromUnix(secondsToMilliseconds(monitoringStatusUpdatedTimestamp)) })}
                </div>
              )}
            </div>
            <div className={classes.paneBody}>
              <div className={classes.healthContainer}>
                <div className={classes.healthStatusCol}>
                  <div className={classes.heading}>
                    {intl.get('healthStatusWithColon')}
                  </div>
                  <div className={`${classes.healthIconContainer} ${errorCount ? classes.orange : classes.green}`}>
                    {errorCount ? <Icon classes={['icon-exclamation-outline']}/> : <Icon classes={['icon-good']}/>}
                  </div>
                  {errorCount ? (
                    <div className={classes.flexColCenter}>
                      <div>{intl.get('warning')}</div>
                      <div
                        className={`${classes.errorCountContainer} ${classes.smallGray} ${classes.italic}`}
                      >
                        {errorCount === 1 ? intl.get('error1Found') : intl.get('errorManyFound', { errorCount })}
                      </div>
                    </div>
                  ) : (
                    <div>
                      {intl.get('good')}
                    </div>
                  )}
                </div>
                <div className={classes.healthDetailsCol}>
                  <div className={classes.tableContainer}>
                    <table className={classes.table}>
                      <thead>
                      <tr>
                        <th className={classes.itemColumn}/>
                        <th className={`${classes.statusColumn} ${classes.heading}`}>
                          {intl.get('status')}
                        </th>
                        <th className={`${classes.dateColumn} ${classes.heading}`}>
                          {intl.get('tableHeaderDate')}
                        </th>
                        <th className={classes.heading}>
                          {intl.get('details')}
                        </th>
                      </tr>
                      </thead>
                      <tbody>
                        <MonitoringRow
                          icon={<Icon classes={['icon-room-system']}/>}
                          label={intl.get('availability')}
                          isOk={_get(deviceMonitoring, 'metrics.online.value')}
                          timestamp={_get(deviceMonitoring, 'metrics.online.timestamp')}
                          errorTitle={intl.get('systemDisconnected')}
                          errorDescription={intl.get('systemDisconnectedMoreThan5Minutes')}
                        />
                        <MonitoringRow
                          icon={<Icon classes={['icon-ic-display']}/>}
                          label={intl.get('display')}
                          isOk={_get(deviceMonitoring, 'metrics.displayConnected.value')}
                          timestamp={_get(deviceMonitoring, 'metrics.displayConnected.timestamp')}
                          errorTitle={intl.get('displayDisconnected')}
                          errorDescription={intl.get('displayDisconnectedMoreThan5Minutes')}
                        />
                        <MonitoringRow
                          icon={<Icon classes={['icon-video']}/>}
                          label={intl.get('camera')}
                          isOk={_get(deviceMonitoring, 'metrics.cameraConnected.value')}
                          timestamp={_get(deviceMonitoring, 'metrics.cameraConnected.timestamp')}
                          errorTitle={intl.get('cameraDisconnected')}
                          errorDescription={intl.get('cameraDisconnectedMoreThan5Minutes')}
                        />
                        <MonitoringRow
                          icon={<Icon classes={['icon-mic']}/>}
                          label={intl.get('microphone')}
                          isOk={_get(deviceMonitoring, 'metrics.micConnected.value')}
                          timestamp={_get(deviceMonitoring, 'metrics.micConnected.timestamp')}
                          errorTitle={intl.get('microphoneDisconnected')}
                          errorDescription={intl.get('microphoneDisconnectedMoreThan5Minutes')}
                        />
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showReboot && <div className={classes.pane}>
        <div className={classes.contentContainer}>
          <div className={classes.paneTitle}>
            {intl.get('rebootSystem')}
          </div>
          <div className={classes.paneBody}>
            <div className={classes.textContainer}>{intl.get('rebootYourRoomSystem')}</div>
            <div className={classes.buttonContainer}>
              <Button
                id="connect"
                name="btnConnect"
                onClick={reboot}
                role="button"
                type="button"
                disabled={!canReboot}
              >
                {intl.get('reboot')}
              </Button>
            </div>
          </div>
        </div>
      </div>}

      {showReset && <div className={classes.pane}>
        <div className={classes.contentContainer}>
          <div className={classes.paneTitle}>
            {intl.get('resetSystemSettings')}
          </div>
          <div className={classes.paneBody}>
            <div className={classes.textContainer}>{intl.get('resetYourSystem')}</div>
            <div className={classes.buttonContainer}>
              <Button
                id="connect"
                name="btnConnect"
                onClick={reset}
                role="button"
                type="button"
                disabled={!canReset}
              >
                {intl.get('reset')}
              </Button>
            </div>
          </div>
        </div>
      </div>}

      {canPerformUpgradeManagement && (
        <div className={classes.pane}>
          <div className={classes.contentContainer}>
            <div className={classes.paneHeader}>
              <div className={classes.paneTitle}>
                {intl.get('upgradeManagement')}
              </div>
              <div className={classes.paneHeaderStatus}>
                {intl.get('runningVersion', { version: '1.1.2' })}
              </div>
            </div>
            <div className={classes.paneBody}>
              <div className={`${classes.upgradeIcon} ${classes.orange}`}>
                <Icon classes={['icon-admin']}/>
              </div>
              <div className={classes.textContainer}>{intl.get('noHotFixes')}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaintenanceTabComponent;
